// src/utils/contractApi.js

export const CONTRACT_INSIGHT_MAX_FILE_MB = 200;
export const CONTRACT_INSIGHT_MAX_FILE_BYTES =
  CONTRACT_INSIGHT_MAX_FILE_MB * 1024 * 1024;

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;

// Ordinary authenticated backend requests, such as creating a job or reading status.
const DEFAULT_TIMEOUT_MS = 45 * SECOND_MS;

// Actual browser-to-GCS transfer timeout for a contract file.
const DEFAULT_UPLOAD_TIMEOUT_MS = 60 * MINUTE_MS;

// How long the frontend waits for the backend to accept the analyze request.
const DEFAULT_START_TIMEOUT_MS = 45 * SECOND_MS;

// How long the frontend waits for the backend to issue a signed upload URL.
const DEFAULT_UPLOAD_URL_TIMEOUT_MS = 90 * SECOND_MS;

// How long the frontend waits for the backend to confirm a completed GCS upload.
const DEFAULT_UPLOAD_COMPLETE_TIMEOUT_MS = 90 * SECOND_MS;

// Backend deployment requirement:
// CONTRACT_INSIGHT_UPLOAD_URL_TTL_SECONDS=3600
// This keeps the signed GCS upload URL valid for the same 60-minute window.

const HOST = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
const BASE_URL = HOST ? `${HOST}/contract` : "/contract";

function buildUrl(path) {
  return `${BASE_URL}${path}`;
}

function buildBackendUrl(path) {
  const cleanPath = String(path || "").startsWith("/") ? String(path || "") : `/${String(path || "")}`;
  return HOST ? `${HOST}${cleanPath}` : cleanPath;
}

function makeClientNonce() {
  return (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}_${Math.random().toString(16).slice(2)}`
  );
}

function getLocale() {
  if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  return "en-US";
}

function requireJobId(jobId) {
  const value = String(jobId || "").trim();
  if (!value) throw new Error("Missing jobId.");
  return value;
}

function requireContractInsightBrowserArtifactKey(artifactKey) {
  const value = String(artifactKey || "").trim().toLowerCase();
  if (!["summary", "page-review", "terms-guide"].includes(value)) {
    throw new Error("Invalid Contract Insight report artifact.");
  }
  return value;
}

function requireIdToken(opts = {}, label = "Contract Insight API") {
  const token = String(opts.idToken || "").trim();
  if (!token) {
    throw new Error(`${label} requires a valid Firebase ID token. Please log in again.`);
  }
  return token;
}

export function assertContractInsightUploadFile(file) {
  if (!file) {
    throw new Error("Please attach a contract file.");
  }

  const sizeBytes = Number(file.size || 0);
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    const err = new Error("File size is missing or invalid.");
    err.name = "ContractInsightUploadError";
    err.code = "CONTRACT_INSIGHT_INVALID_FILE_SIZE";
    err.uploadStage = "upload_validation";
    err.failureStage = "upload_validation";
    err.phase = "upload_validation";
    err.userMessage = err.message;
    err.technicalMessage = err.message;
    err.status = 400;
    err.statusCode = 400;
    err.fileName = file?.name || "contract";
    err.sizeBytes = sizeBytes;
    throw err;
  }

  if (sizeBytes > CONTRACT_INSIGHT_MAX_FILE_BYTES) {
    const message =
      `File is too large. Maximum allowed is ${CONTRACT_INSIGHT_MAX_FILE_MB} MB.`;
    const err = new Error(message);
    err.name = "ContractInsightUploadError";
    err.code = "CONTRACT_INSIGHT_FILE_TOO_LARGE";
    err.uploadStage = "upload_validation";
    err.failureStage = "upload_validation";
    err.phase = "upload_validation";
    err.userMessage = message;
    err.technicalMessage = message;
    err.status = 413;
    err.statusCode = 413;
    err.fileName = file?.name || "contract";
    err.sizeBytes = sizeBytes;
    err.maxFileMb = CONTRACT_INSIGHT_MAX_FILE_MB;
    err.maxFileBytes = CONTRACT_INSIGHT_MAX_FILE_BYTES;
    throw err;
  }

  return sizeBytes;
}

function authHeaders(idToken, extra = {}) {
  const headers = { ...extra };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  return headers;
}

function withTimeout(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const ms = Math.max(1000, Number(timeoutMs || DEFAULT_TIMEOUT_MS));
  const timer = setTimeout(() => controller.abort(), ms);
  return { controller, timer };
}

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function durationMs(startedAt) {
  return Math.max(0, Math.round(nowMs() - Number(startedAt || 0)));
}

function safeInvoke(callback, payload) {
  if (typeof callback !== "function") return;
  try {
    callback(payload);
  } catch (err) {
    console.warn("Contract Insight callback failed:", err);
  }
}

function sanitizeSignedUploadHeaders(headers = {}) {
  const safeHeaders = {};
  Object.entries(headers || {}).forEach(([key, value]) => {
    const normalizedKey = String(key || "").trim();
    const lowerKey = normalizedKey.toLowerCase();
    if (!normalizedKey || value === undefined || value === null || value === "") return;
    if (lowerKey === "authorization" || lowerKey === "cookie" || lowerKey === "content-length") return;
    safeHeaders[normalizedKey] = String(value);
  });
  return safeHeaders;
}

function normalizeApiErrorMessage(data, res, fallbackLabel) {
  const status = Number(res?.status || 0);
  const errorObject = data?.error && typeof data.error === "object" ? data.error : null;
  const backendMessage =
    (typeof data?.error === "string" ? data.error : "") ||
    errorObject?.reason ||
    errorObject?.message ||
    errorObject?.code ||
    data?.message ||
    data?.reason ||
    data?.detail ||
    data?.billing?.error ||
    "";

  if (status === 401) return backendMessage || "Your login session expired. Please log in again.";
  if (status === 402) return backendMessage || "No Contract Insight report package is available on this account.";
  if (status === 403) return backendMessage || "You do not have access to this Contract Insight job.";
  if (status === 404) return backendMessage || "The requested Contract Insight job was not found.";
  if (status === 409) return backendMessage || "This Contract Insight job is already locked or processing.";
  if (status === 413) return backendMessage || "This contract file is too large for Contract Insight.";
  return backendMessage || `${fallbackLabel} request failed (HTTP ${status || "UNKNOWN"}).`;
}

async function parseJsonResponse(res, fallbackLabel = "Contract Insight API") {
  const contentType = res.headers?.get?.("content-type") || "";
  let data = null;
  let rawText = "";

  try {
    if (contentType.toLowerCase().includes("application/json")) {
      data = await res.json();
    } else {
      rawText = await res.text();
      if (rawText) {
        try {
          data = JSON.parse(rawText);
        } catch {
          data = null;
        }
      }
    }
  } catch {
    data = null;
  }

  if (!res.ok || data?.ok === false) {
    const reason = normalizeApiErrorMessage(data, res, fallbackLabel);
    const err = new Error(reason);
    err.status = res.status;
    err.statusCode = res.status;
    err.response = data;
    err.rawText = rawText;
    err.billing = data?.billing || data?.error?.billingSnapshot;
    err.code = data?.error?.code || data?.code;
    err.job = data?.job;
    throw err;
  }

  if (data === null) {
    throw new Error(`${fallbackLabel} returned a non-JSON response (status ${res.status}).`);
  }

  return data;
}

async function fetchJson(path, options = {}, fallbackLabel = "Contract Insight API") {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    idToken,
    requireAuth = true,
    ...fetchOptions
  } = options;

  const token = requireAuth ? requireIdToken({ idToken }, fallbackLabel) : String(idToken || "").trim();
  const { controller, timer } = withTimeout(timeoutMs);

  try {
    const res = await fetch(buildUrl(path), {
      credentials: "include",
      ...fetchOptions,
      signal: controller.signal,
      headers: authHeaders(token, fetchOptions.headers || {}),
    });
    return await parseJsonResponse(res, fallbackLabel);
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(`${fallbackLabel} timed out. Please try again.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function isAlreadyLockedOrStartedMessage(value) {
  const text = String(value || "").toLowerCase();
  return (
    text.includes("already locked") ||
    text.includes("already queued") ||
    text.includes("already started") ||
    text.includes("already processing") ||
    text.includes("locked for analysis") ||
    text.includes("queued for analysis") ||
    text.includes("processing")
  );
}

export function isContractInsightAlreadyStartedError(err) {
  if (!err) return false;
  const status = Number(err.status || err.statusCode || 0);
  if (status !== 400 && status !== 409) return false;

  return (
    isAlreadyLockedOrStartedMessage(err.message) ||
    isAlreadyLockedOrStartedMessage(err.response?.error) ||
    isAlreadyLockedOrStartedMessage(err.response?.message) ||
    isAlreadyLockedOrStartedMessage(err.response?.reason) ||
    isAlreadyLockedOrStartedMessage(err.response?.detail)
  );
}

function normalizeJobStatusPayload(data, fallbackJobId = "") {
  const job = data?.job || data || {};
  const progress = data?.progress || job?.progress || {};
  const status = data?.status || job?.status || progress?.stage || "";

  return {
    ...data,
    job,
    progress,
    status,
    job_id: data?.job_id || data?.jobId || job?.job_id || job?.jobId || fallbackJobId,
    jobId: data?.jobId || data?.job_id || job?.jobId || job?.job_id || fallbackJobId,
  };
}

function buildUploadUserMessage(stage, fileName = "this contract") {
  const name = String(fileName || "this contract");
  if (stage === "file_upload_timeout") {
    return `${name} took too long to upload. Please try again or use a smaller file.`;
  }
  if (stage === "file_upload") {
    return `We couldn’t upload ${name}. Please check your internet connection and try again.`;
  }
  if (stage === "upload_confirmation") {
    return `${name} was uploaded, but we couldn’t confirm it. Please try again.`;
  }
  if (stage === "missing_file") {
    return `We couldn’t access ${name}. Please select the file again.`;
  }
  return `We couldn’t prepare ${name} for upload. Please try again.`;
}

function createUploadStageError(stage, cause, context = {}) {
  const fileName = context.fileName || context.originalName || "this contract";
  const userMessage = context.userMessage || buildUploadUserMessage(stage, fileName);
  const err = new Error(userMessage);
  err.name = "ContractInsightUploadError";
  err.code = "CONTRACT_INSIGHT_UPLOAD_FAILED";
  err.uploadStage = stage;
  err.failureStage = stage;
  err.phase = stage;
  err.userMessage = userMessage;
  err.technicalMessage = String(cause?.technicalMessage || cause?.message || cause || userMessage);
  err.jobId = context.jobId || "";
  err.fileName = fileName;
  err.sizeBytes = Number(context.sizeBytes || 0);
  err.elapsedMs = Number(context.elapsedMs || 0);
  err.timings = context.timings || {};
  err.status = Number(cause?.status || cause?.statusCode || 0) || undefined;
  err.statusCode = err.status;
  err.response = cause?.response;
  err.rawText = cause?.rawText;
  err.originalError = cause || null;
  return err;
}

export function getContractInsightUploadUserMessage(err) {
  if (!err) return "The upload could not be completed. Please try again.";
  if (err.userMessage) return String(err.userMessage);
  if (err.uploadStage || err.failureStage) {
    return buildUploadUserMessage(err.uploadStage || err.failureStage, err.fileName || "this contract");
  }
  return err.message || "The upload could not be completed. Please try again.";
}

export async function createContractInsightJob(payload, opts = {}) {
  return fetchJson(
    "/job",
    {
      method: "POST",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        payload,
        locale: opts.locale || getLocale(),
        client_nonce: opts.clientNonce || opts.client_nonce || makeClientNonce(),
        clientNonce: opts.clientNonce || opts.client_nonce || undefined,
      }),
    },
    "Create Contract Insight job"
  );
}

export async function abandonContractInsightJob(jobId, details = {}, opts = {}) {
  const id = requireJobId(jobId);
  const clientAbandonedAt = new Date().toISOString();
  return fetchJson(
    `/job/${encodeURIComponent(id)}/abandon`,
    {
      method: "POST",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reason: String(details.reason || details.message || "upload_session_failed"),
        failure_stage: String(details.failureStage || details.failure_stage || "pre_analysis_failure"),
        failureStage: String(details.failureStage || details.failure_stage || "pre_analysis_failure"),
        file_name: details.fileName || details.file_name || "",
        fileName: details.fileName || details.file_name || "",
        elapsed_ms: Number(details.elapsedMs || details.elapsed_ms || 0),
        elapsedMs: Number(details.elapsedMs || details.elapsed_ms || 0),
        client_abandoned_at: clientAbandonedAt,
        clientAbandonedAt,
      }),
    },
    "Close failed Contract Insight upload session"
  );
}

async function requestUploadUrl(jobId, fileMeta, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/job/${encodeURIComponent(id)}/upload-url`,
    {
      method: "POST",
      timeoutMs: opts.uploadUrlTimeoutMs ?? DEFAULT_UPLOAD_URL_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fileMeta || {}),
    },
    "Create Contract Insight upload URL"
  );
}

async function putFileToSignedUrl(uploadUrl, file, headers = {}, opts = {}) {
  if (!uploadUrl) throw new Error("Missing signed upload URL.");
  if (!file) throw new Error("Missing contract file for signed upload.");

  const timeoutMs = Math.max(1000, Number(opts.timeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS));
  const startedAt = nowMs();
  const totalBytes = Number(file.size || 0);
  const fileName = file.name || "contract";

  const emitProgress = (loadedBytes) => {
    const loaded = Math.max(0, Number(loadedBytes || 0));
    const elapsedMs = durationMs(startedAt);
    const percent = totalBytes > 0 ? Math.round((loaded / totalBytes) * 100) : 0;
    safeInvoke(opts.onProgress, {
      type: "upload_progress",
      phase: "uploading_file",
      fileName,
      loadedBytes: loaded,
      totalBytes,
      percent: Math.max(0, Math.min(100, percent)),
      elapsedMs,
      averageBytesPerSecond: elapsedMs > 0 ? Math.round((loaded * 1000) / elapsedMs) : 0,
    });
  };

  if (typeof XMLHttpRequest !== "undefined") {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        fn(value);
      };

      xhr.open("PUT", uploadUrl, true);
      xhr.timeout = timeoutMs;
      const safeHeaders = sanitizeSignedUploadHeaders({
        "Content-Type": file.type || "application/octet-stream",
        ...(headers || {}),
      });
      Object.entries(safeHeaders).forEach(([key, value]) => xhr.setRequestHeader(key, String(value)));

      xhr.upload.onprogress = (event) => emitProgress(event.loaded);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          emitProgress(totalBytes);
          finish(resolve, {
            ok: true,
            status: xhr.status,
            elapsedMs: durationMs(startedAt),
          });
          return;
        }
        const detail = String(xhr.responseText || "").slice(0, 300);
        const error = new Error(`Signed upload failed for ${fileName} (HTTP ${xhr.status})${detail ? `: ${detail}` : ""}.`);
        error.status = xhr.status;
        finish(reject, error);
      };
      xhr.onerror = () => finish(reject, new Error(`Signed upload network failure for ${fileName}.`));
      xhr.ontimeout = () => {
        const error = new Error(`Signed upload timed out for ${fileName}.`);
        error.code = "UPLOAD_TIMEOUT";
        error.isTimeout = true;
        finish(reject, error);
      };
      xhr.onabort = () => {
        const error = new Error(`Signed upload was stopped for ${fileName}.`);
        error.code = "UPLOAD_ABORTED";
        finish(reject, error);
      };
      xhr.send(file);
    });
  }

  const { controller, timer } = withTimeout(timeoutMs);
  try {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      signal: controller.signal,
      headers: sanitizeSignedUploadHeaders({
        "Content-Type": file.type || "application/octet-stream",
        ...(headers || {}),
      }),
      body: file,
    });
    if (!res.ok) throw new Error(`Signed upload failed for ${fileName} (HTTP ${res.status}).`);
    emitProgress(totalBytes);
    return { ok: true, status: res.status, elapsedMs: durationMs(startedAt) };
  } catch (err) {
    if (err?.name === "AbortError") {
      const timeoutError = new Error(`Signed upload timed out for ${fileName}.`);
      timeoutError.code = "UPLOAD_TIMEOUT";
      timeoutError.isTimeout = true;
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function markUploadComplete(jobId, fileId, storagePath, fileMeta = {}, opts = {}) {
  const id = requireJobId(jobId);
  if (!fileId) throw new Error("Missing fileId.");
  if (!storagePath) throw new Error("Missing storagePath.");

  return fetchJson(
    `/job/${encodeURIComponent(id)}/upload-complete`,
    {
      method: "POST",
      timeoutMs: opts.uploadCompleteTimeoutMs ?? DEFAULT_UPLOAD_COMPLETE_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        file_id: fileId,
        fileId,
        storage_path: storagePath,
        storagePath,
        original_name: fileMeta.original_name || fileMeta.originalName || "",
        originalName: fileMeta.originalName || fileMeta.original_name || "",
        content_type: fileMeta.content_type || fileMeta.contentType || "application/octet-stream",
        contentType: fileMeta.contentType || fileMeta.content_type || "application/octet-stream",
        size_bytes: Number(fileMeta.size_bytes ?? fileMeta.sizeBytes ?? 0),
        sizeBytes: Number(fileMeta.sizeBytes ?? fileMeta.size_bytes ?? 0),
        page_count: Number(fileMeta.page_count ?? fileMeta.pageCount ?? 0),
        pageCount: Number(fileMeta.pageCount ?? fileMeta.page_count ?? 0),
        detected_page_count: Number(fileMeta.detected_page_count ?? fileMeta.detectedPageCount ?? 0),
        detectedPageCount: Number(fileMeta.detectedPageCount ?? fileMeta.detected_page_count ?? 0),
        manual_page_count: Number(fileMeta.manual_page_count ?? fileMeta.manualPageCount ?? 0),
        manualPageCount: Number(fileMeta.manualPageCount ?? fileMeta.manual_page_count ?? 0),
        manual_page_count_required: Boolean(fileMeta.manual_page_count_required || fileMeta.manualPageCountRequired),
        manualPageCountRequired: Boolean(fileMeta.manualPageCountRequired || fileMeta.manual_page_count_required),
        page_count_status: fileMeta.page_count_status || fileMeta.pageCountStatus || "unknown",
        pageCountStatus: fileMeta.pageCountStatus || fileMeta.page_count_status || "unknown",
        page_count_source: fileMeta.page_count_source || fileMeta.pageCountSource || "unknown",
        pageCountSource: fileMeta.pageCountSource || fileMeta.page_count_source || "unknown",
        schema_id: fileMeta.schema_id ?? fileMeta.schemaId ?? null,
        schemaId: fileMeta.schemaId ?? fileMeta.schema_id ?? null,
        billing_product: "contract_insight",
        billingProduct: "contract_insight",
        requested_page_count: Number(fileMeta.requested_page_count ?? fileMeta.requestedPageCount ?? 0),
        requestedPageCount: Number(fileMeta.requestedPageCount ?? fileMeta.requested_page_count ?? 0),
        declared_total_page_count: Number(fileMeta.declared_total_page_count ?? fileMeta.declaredTotalPageCount ?? 0),
        declaredTotalPageCount: Number(fileMeta.declaredTotalPageCount ?? fileMeta.declared_total_page_count ?? 0),
        selected_page_tier: Number(fileMeta.selected_page_tier ?? fileMeta.selectedPageTier ?? 0) || null,
        selectedPageTier: Number(fileMeta.selectedPageTier ?? fileMeta.selected_page_tier ?? 0) || null,
      }),
    },
    "Confirm Contract Insight upload"
  );
}

export async function uploadContractInsightFile(jobId, file, fileMeta = {}, opts = {}) {
  const id = requireJobId(jobId);
  requireIdToken(opts, "Upload Contract Insight file");
  const fileSizeBytes = assertContractInsightUploadFile(file);

  const startedAt = nowMs();
  const timings = { upload_url_ms: 0, file_upload_ms: 0, upload_complete_ms: 0, total_ms: 0 };
  const meta = {
    ...fileMeta,
    original_name: file.name || "contract.bin",
    originalName: file.name || "contract.bin",
    content_type: file.type || "application/octet-stream",
    contentType: file.type || "application/octet-stream",
    size_bytes: fileSizeBytes,
    sizeBytes: fileSizeBytes,
  };

  safeInvoke(opts.onUploadEvent, {
    type: "file_start",
    phase: "requesting_upload_url",
    jobId: id,
    fileName: file.name,
    sizeBytes: fileSizeBytes,
    percent: 0,
  });

  try {
    const uploadUrlStartedAt = nowMs();
    let ticket;
    try {
      ticket = await requestUploadUrl(id, meta, opts);
      timings.upload_url_ms = durationMs(uploadUrlStartedAt);
    } catch (cause) {
      throw createUploadStageError("upload_url", cause, {
        jobId: id,
        fileName: file.name,
        sizeBytes: fileSizeBytes,
        elapsedMs: durationMs(startedAt),
        timings,
      });
    }

    const uploadUrl = ticket?.upload_url || ticket?.uploadUrl || ticket?.url;
    const fileId = ticket?.file_id || ticket?.fileId || ticket?.file?.fileId || ticket?.file?.file_id;
    const storagePath = ticket?.storage_path || ticket?.storagePath || ticket?.file?.storagePath || ticket?.file?.storage_path;
    if (!uploadUrl || !fileId || !storagePath) {
      throw createUploadStageError("upload_url_response", new Error("Upload URL response is incomplete."), {
        jobId: id,
        fileName: file.name,
        sizeBytes: fileSizeBytes,
        elapsedMs: durationMs(startedAt),
        timings,
      });
    }

    safeInvoke(opts.onUploadEvent, {
      type: "phase_start",
      phase: "uploading_file",
      jobId: id,
      fileName: file.name,
      sizeBytes: fileSizeBytes,
      fileId,
      storagePath,
      percent: 0,
    });

    const fileUploadStartedAt = nowMs();
    try {
      await putFileToSignedUrl(uploadUrl, file, ticket?.headers || {}, {
        timeoutMs: opts.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS,
        onProgress: (event) => safeInvoke(opts.onUploadEvent, {
          ...event,
          jobId: id,
          fileId,
          storagePath,
        }),
      });
      timings.file_upload_ms = durationMs(fileUploadStartedAt);
    } catch (cause) {
      timings.file_upload_ms = durationMs(fileUploadStartedAt);
      throw createUploadStageError(cause?.isTimeout ? "file_upload_timeout" : "file_upload", cause, {
        jobId: id,
        fileName: file.name,
        sizeBytes: fileSizeBytes,
        elapsedMs: durationMs(startedAt),
        timings,
      });
    }

    safeInvoke(opts.onUploadEvent, {
      type: "phase_start",
      phase: "confirming_upload",
      jobId: id,
      fileName: file.name,
      sizeBytes: fileSizeBytes,
      fileId,
      storagePath,
      percent: 100,
    });

    const confirmStartedAt = nowMs();
    let confirmed;
    try {
      confirmed = await markUploadComplete(id, fileId, storagePath, meta, opts);
      timings.upload_complete_ms = durationMs(confirmStartedAt);
    } catch (cause) {
      timings.upload_complete_ms = durationMs(confirmStartedAt);
      throw createUploadStageError("upload_confirmation", cause, {
        jobId: id,
        fileName: file.name,
        sizeBytes: fileSizeBytes,
        elapsedMs: durationMs(startedAt),
        timings,
      });
    }

    timings.total_ms = durationMs(startedAt);
    const result = {
      ...confirmed,
      ok: true,
      job_id: id,
      jobId: id,
      file_id: fileId,
      fileId,
      storage_path: storagePath,
      storagePath,
      upload_timings: timings,
      uploadTimings: timings,
    };

    safeInvoke(opts.onUploadEvent, {
      type: "file_complete",
      phase: "completed",
      jobId: id,
      fileName: file.name,
      sizeBytes: fileSizeBytes,
      fileId,
      storagePath,
      percent: 100,
      elapsedMs: timings.total_ms,
      result,
    });

    return result;
  } catch (cause) {
    const error = cause?.name === "ContractInsightUploadError"
      ? cause
      : createUploadStageError("file_upload", cause, {
          jobId: id,
          fileName: file.name,
          sizeBytes: fileSizeBytes,
          elapsedMs: durationMs(startedAt),
          timings,
        });
    safeInvoke(opts.onUploadEvent, {
      type: "file_error",
      phase: "failed",
      jobId: id,
      fileName: file.name,
      failureStage: error.failureStage,
      userMessage: error.userMessage,
      technicalMessage: error.technicalMessage,
      error,
    });
    throw error;
  }
}

export async function startContractInsightRun(jobId, payload, opts = {}) {
  const id = requireJobId(jobId);
  try {
    const data = await fetchJson(
      `/job/${encodeURIComponent(id)}/analyze`,
      {
        method: "POST",
        timeoutMs: opts.timeoutMs ?? DEFAULT_START_TIMEOUT_MS,
        idToken: opts.idToken,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payload,
          locale: opts.locale || getLocale(),
          client_nonce: opts.clientNonce || opts.client_nonce || makeClientNonce(),
          clientNonce: opts.clientNonce || opts.client_nonce || undefined,
        }),
      },
      "Start Contract Insight job"
    );
    return normalizeJobStatusPayload(data, id);
  } catch (err) {
    if (isContractInsightAlreadyStartedError(err)) {
      return normalizeJobStatusPayload({
        ok: true,
        alreadyStarted: true,
        alreadyLocked: true,
        job_id: id,
        jobId: id,
        status: err?.response?.status || err?.job?.status || "queued",
        message: err?.response?.message || err?.response?.error || "This Contract Insight job is already queued or processing.",
        job: err?.job || err?.response?.job || { job_id: id, jobId: id, status: "queued" },
      }, id);
    }
    throw err;
  }
}

export async function getContractInsightStatus(jobId, opts = {}) {
  const id = requireJobId(jobId);
  try {
    const data = await fetchJson(
      `/status/${encodeURIComponent(id)}`,
      {
        method: "GET",
        timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        idToken: opts.idToken,
      },
      "Get Contract Insight status"
    );
    return normalizeJobStatusPayload(data, id);
  } catch (err) {
    if (Number(err?.status || err?.statusCode || 0) !== 404) throw err;
    const data = await getReport(id, opts);
    return normalizeJobStatusPayload(data, id);
  }
}

export async function getReport(jobId, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/report/${encodeURIComponent(id)}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "Get Contract Insight report"
  );
}


function getNestedValue(obj, path) {
  const parts = String(path || "").split(".").filter(Boolean);
  let current = obj;

  for (const part of parts) {
    if (current === undefined || current === null) return "";
    current = current?.[part];
  }

  return current === undefined || current === null ? "" : current;
}

function firstArtifactValue(data, paths = []) {
  for (const path of paths) {
    const value = getNestedValue(data, path);
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function optionalCoverageCount(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(0, Math.trunc(value));
    }

    if (typeof value === "string" && value.trim() !== "") {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return Math.max(0, Math.trunc(numeric));
      }
    }
  }

  return null;
}

function normalizeContractInsightCoverageSummary(data = {}) {
  const report =
    data?.report && typeof data.report === "object" && !Array.isArray(data.report)
      ? data.report
      : {};

  const coverageCandidates = [
    getNestedValue(report, "summaryReport.executiveSnapshot.coverage", null),
    getNestedValue(report, "summary_report.executive_snapshot.coverage", null),
    getNestedValue(data, "report.summaryReport.executiveSnapshot.coverage", null),
    getNestedValue(data, "report.summary_report.executive_snapshot.coverage", null),
    getNestedValue(data, "summaryReport.executiveSnapshot.coverage", null),
    getNestedValue(data, "summary_report.executive_snapshot.coverage", null),
  ];

  const coverage =
    coverageCandidates.find(
      (value) =>
        value &&
        typeof value === "object" &&
        !Array.isArray(value),
    ) || {};

  const summaryCandidates = [
    report?.summary,
    data?.report?.summary,
    data?.summary,
  ];

  const summary =
    summaryCandidates.find(
      (value) =>
        value &&
        typeof value === "object" &&
        !Array.isArray(value),
    ) || {};

  const found = optionalCoverageCount(
    coverage?.found,
    coverage?.present,
    summary?.criteriaPresent,
    summary?.criteria_present,
  );

  const unclear = optionalCoverageCount(
    coverage?.unclear,
    coverage?.partial,
    summary?.criteriaUnclear,
    summary?.criteria_unclear,
  );

  const notConfirmed = optionalCoverageCount(
    coverage?.notConfirmed,
    coverage?.not_confirmed,
    coverage?.unknown,
    summary?.criteriaNotConfirmed,
    summary?.criteria_not_confirmed,
    summary?.criteriaUnknown,
    summary?.criteria_unknown,
  );

  const notFound = optionalCoverageCount(
    coverage?.notFound,
    coverage?.not_found,
    coverage?.absent,
    summary?.criteriaNotFound,
    summary?.criteria_not_found,
    summary?.criteriaAbsent,
    summary?.criteria_absent,
  );

  const total = optionalCoverageCount(
    coverage?.total,
    summary?.criteriaTotal,
    summary?.criteria_total,
  );

  if ([found, unclear, notConfirmed, notFound, total].every((value) => value === null)) {
    return {};
  }

  const normalized = {};

  if (found !== null) {
    normalized.found = found;
    normalized.present = found;
  }

  if (unclear !== null) {
    normalized.unclear = unclear;
  }

  if (notConfirmed !== null) {
    normalized.notConfirmed = notConfirmed;
    normalized.not_confirmed = notConfirmed;
    normalized.unknown = notConfirmed;
  }

  if (notFound !== null) {
    normalized.notFound = notFound;
    normalized.not_found = notFound;
    normalized.absent = notFound;
  }

  if (total !== null) {
    normalized.total = total;
  }

  normalized.labels = {
    found: "Found",
    unclear: "Unclear",
    notConfirmed: "Not Confirmed",
    notFound: "Not Found",
  };

  return normalized;
}

export function getContractInsightReportArtifactLinks(data = {}) {
  const summaryReportUrl = firstArtifactValue(data, [
    "reportPdfUrl",
    "report_pdf_url",
    "reportPdfSignedUrl",
    "report_pdf_signed_url",
    "primarySummaryReportPdfUrl",
    "primary_summary_report_pdf_url",
    "artifacts.reportPdfUrl",
    "artifacts.report_pdf_url",
    "artifacts.primarySummaryReportPdfUrl",
    "artifacts.primary_summary_report_pdf_url",
    "artifacts.primarySummaryReportPdf.url",
    "artifacts.primary_summary_report_pdf.url",
    "artifacts.reportPdf.url",
    "artifacts.report_pdf.url",
    "reportArtifacts.pdf.url",
    "report_artifacts.pdf.url",
    "pdf.url",
    "fileVaultUrl",
    "file_vault_url",
    "fileVault.pdf.url",
    "file_vault.pdf.url",
    "job.reportPdfUrl",
    "job.report_pdf_url",
    "job.artifacts.reportPdfUrl",
    "job.artifacts.report_pdf_url",
    "job.report.reportPdfUrl",
    "job.report.report_pdf_url",
  ]);

  const pageReviewUrl = firstArtifactValue(data, [
    "liveViewPdfUrl",
    "live_view_pdf_url",
    "pageReviewPdfUrl",
    "page_review_pdf_url",
    "artifacts.liveViewPdfUrl",
    "artifacts.live_view_pdf_url",
    "artifacts.pageReviewPdfUrl",
    "artifacts.page_review_pdf_url",
    "artifacts.liveViewPdf.url",
    "artifacts.live_view_pdf.url",
    "reportArtifacts.liveViewPdf.url",
    "report_artifacts.live_view_pdf.url",
    "liveViewPdf.url",
    "live_view_pdf.url",
    "job.liveViewPdfUrl",
    "job.live_view_pdf_url",
    "job.artifacts.liveViewPdfUrl",
    "job.artifacts.live_view_pdf_url",
    "job.report.liveViewPdfUrl",
    "job.report.live_view_pdf_url",
  ]);

  const termsGuideUrl = firstArtifactValue(data, [
    "terminologyPdfUrl",
    "terminology_pdf_url",
    "terminologyReferencePdfUrl",
    "terminology_reference_pdf_url",
    "termsGuidePdfUrl",
    "terms_guide_pdf_url",
    "artifacts.terminologyPdfUrl",
    "artifacts.terminology_pdf_url",
    "artifacts.terminologyReferencePdfUrl",
    "artifacts.terminology_reference_pdf_url",
    "artifacts.terminologyPdf.url",
    "artifacts.terminology_pdf.url",
    "reportArtifacts.terminologyPdf.url",
    "report_artifacts.terminology_pdf.url",
    "terminologyPdf.url",
    "terminology_pdf.url",
    "job.terminologyPdfUrl",
    "job.terminology_pdf_url",
    "job.artifacts.terminologyPdfUrl",
    "job.artifacts.terminology_pdf_url",
    "job.report.terminologyPdfUrl",
    "job.report.terminology_pdf_url",
  ]);

  const summaryReportPath = firstArtifactValue(data, [
    "reportPdfPath",
    "report_pdf_path",
    "primarySummaryReportPdfPath",
    "primary_summary_report_pdf_path",
    "artifacts.reportPdfPath",
    "artifacts.report_pdf_path",
    "artifacts.primarySummaryReportPdfPath",
    "artifacts.primary_summary_report_pdf_path",
    "artifacts.primarySummaryReportPdf.path",
    "artifacts.primary_summary_report_pdf.path",
    "artifacts.reportPdf.path",
    "artifacts.report_pdf.path",
    "reportArtifacts.pdf.path",
    "report_artifacts.pdf.path",
    "job.reportPdfPath",
    "job.report_pdf_path",
    "job.artifacts.reportPdfPath",
    "job.artifacts.report_pdf_path",
  ]);

  const pageReviewPath = firstArtifactValue(data, [
    "liveViewPdfPath",
    "live_view_pdf_path",
    "pageReviewPdfPath",
    "page_review_pdf_path",
    "artifacts.liveViewPdfPath",
    "artifacts.live_view_pdf_path",
    "artifacts.liveViewPdf.path",
    "artifacts.live_view_pdf.path",
    "reportArtifacts.liveViewPdf.path",
    "report_artifacts.live_view_pdf.path",
    "job.liveViewPdfPath",
    "job.live_view_pdf_path",
    "job.artifacts.liveViewPdfPath",
    "job.artifacts.live_view_pdf_path",
  ]);

  const termsGuidePath = firstArtifactValue(data, [
    "terminologyPdfPath",
    "terminology_pdf_path",
    "terminologyReferencePdfPath",
    "terminology_reference_pdf_path",
    "termsGuidePdfPath",
    "terms_guide_pdf_path",
    "artifacts.terminologyPdfPath",
    "artifacts.terminology_pdf_path",
    "artifacts.terminologyPdf.path",
    "artifacts.terminology_pdf.path",
    "reportArtifacts.terminologyPdf.path",
    "report_artifacts.terminology_pdf.path",
    "job.terminologyPdfPath",
    "job.terminology_pdf_path",
    "job.artifacts.terminologyPdfPath",
    "job.artifacts.terminology_pdf_path",
  ]);

  return {
    summaryReportUrl: summaryReportUrl || "",
    pageReviewUrl: pageReviewUrl || "",
    termsGuideUrl: termsGuideUrl || "",
    summaryReportPath: summaryReportPath || "",
    pageReviewPath: pageReviewPath || "",
    termsGuidePath: termsGuidePath || "",
  };
}

export async function getContractInsightReportArtifacts(jobId, opts = {}) {
  const payload = await getReport(jobId, opts);
  return {
    ...getContractInsightReportArtifactLinks(payload || {}),
    payload: payload || {},
  };
}

export async function createContractInsightBrowserTicket(jobId, artifactKey, opts = {}) {
  const id = requireJobId(jobId);
  const key = requireContractInsightBrowserArtifactKey(artifactKey);
  const label = "Open Contract Insight file";
  const token = requireIdToken(opts, label);
  const requestPath =
    `/files/contract/${encodeURIComponent(id)}/${encodeURIComponent(key)}/browser-ticket`;
  const requestUrl = buildBackendUrl(requestPath);
  const { controller, timer } = withTimeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  try {
    const res = await fetch(requestUrl, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: authHeaders(token),
    });

    const data = await parseJsonResponse(res, label);
    const ticketPath = String(data?.ticketPath || data?.path || "").trim();

    if (!ticketPath) {
      throw new Error("Browser file access did not return an AskLawly ticket.");
    }

    let absoluteRequestUrl = requestUrl;
    if (!/^https?:\/\//i.test(absoluteRequestUrl)) {
      const browserOrigin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "";
      if (!browserOrigin) {
        throw new Error("Browser file access gateway origin is unavailable.");
      }
      absoluteRequestUrl = new URL(absoluteRequestUrl, browserOrigin).toString();
    }

    let browserUrl = "";
    try {
      browserUrl = new URL(ticketPath, absoluteRequestUrl).toString();
    } catch {
      throw new Error("Browser file access returned an invalid AskLawly ticket.");
    }

    return {
      ...data,
      ticketPath,
      browserUrl,
      artifactKey: key,
      jobId: id,
      job_id: id,
    };
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(`${label} timed out. Please try again.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}


export async function listContractThreads(opts = {}) {
  return fetchJson(
    "/threads",
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "List Contract Insight threads"
  );
}

export async function getLiveView(jobId, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/live/${encodeURIComponent(id)}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "Get Contract Insight live view"
  );
}

export async function getSourceFile(jobId, opts = {}) {
  const id = requireJobId(jobId);
  return fetchJson(
    `/source/${encodeURIComponent(id)}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "Get Contract Insight source file"
  );
}

function buildQueryString(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      search.set(key, value ? "true" : "false");
      return;
    }
    search.set(key, String(value));
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

export async function listContractInsightHistory(opts = {}) {
  const requestedPage = Number.parseInt(opts.page ?? 1, 10);
  const requestedPageSize = Number.parseInt(
    opts.pageSize ??
      opts.page_size ??
      opts.limit ??
      opts.maxResults ??
      8,
    10,
  );

  const page = Number.isFinite(requestedPage)
    ? Math.max(1, requestedPage)
    : 1;
  const pageSize = Number.isFinite(requestedPageSize)
    ? Math.max(1, requestedPageSize)
    : 8;

  const queryText = String(opts.query ?? opts.search ?? "").trim();
  const status = String(
    opts.status ??
      opts.statusFilter ??
      opts.status_filter ??
      "all",
  ).trim() || "all";
  const sort = String(
    opts.sort ??
      opts.sortKey ??
      opts.sort_key ??
      "newest",
  ).trim() || "newest";
  const showAll = Boolean(opts.showAll ?? opts.show_all ?? false);
  const includeDeleted = Boolean(
    opts.includeDeleted ?? opts.include_deleted ?? false,
  );

  // Updated history-page default:
  // - page_size is the number of visible rows (normally 8)
  // - lookahead=1 makes the backend inspect one extra row (normally 9 total)
  // - include_stats=0 keeps Next/Previous requests lightweight
  const lookahead = Boolean(
    opts.lookahead ??
      opts.lookAhead ??
      opts.fetchNext ??
      opts.fetch_next ??
      true,
  );
  const includeStats = Boolean(
    opts.includeStats ??
      opts.include_stats ??
      false,
  );

  const query = buildQueryString({
    page,
    page_size: pageSize,

    // Compatibility alias for older deployments. This is the current visible
    // page size only; it is not an overall history-record limit.
    limit: pageSize,

    lookahead: lookahead ? 1 : 0,
    include_stats: includeStats ? 1 : 0,
    query: queryText,
    status,
    sort,
    show_all: showAll,
    include_deleted: includeDeleted,
  });

  return fetchJson(
    `/history${query}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "List Contract Insight history"
  );
}


export async function getContractInsightHistoryStats(opts = {}) {
  const showAll = Boolean(opts.showAll ?? opts.show_all ?? false);
  const includeDeleted = Boolean(
    opts.includeDeleted ?? opts.include_deleted ?? false,
  );

  const query = buildQueryString({
    show_all: showAll,
    include_deleted: includeDeleted,
  });

  return fetchJson(
    `/history/stats${query}`,
    {
      method: "GET",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "Get Contract Insight history stats"
  );
}

export async function getContractInsightHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  try {
    return await fetchJson(
      `/history/${encodeURIComponent(id)}`,
      {
        method: "GET",
        timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        idToken: opts.idToken,
      },
      "Get Contract Insight history"
    );
  } catch (err) {
    if (Number(err?.status || err?.statusCode || 0) !== 404) throw err;
    return getReport(id, opts);
  }
}

export async function renameContractInsightHistory(jobId, title, opts = {}) {
  const id = requireJobId(jobId);
  const cleanTitle = String(title || "").trim();

  if (!cleanTitle) {
    throw new Error("Please enter a name for this Contract Insight analysis.");
  }

  return fetchJson(
    `/history/${encodeURIComponent(id)}/rename`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: cleanTitle,
        name: cleanTitle,
        displayTitle: cleanTitle,
        display_title: cleanTitle,
      }),
    },
    "Rename Contract Insight history"
  );
}

export async function archiveContractInsightHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/history/${encodeURIComponent(id)}/archive`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: true }),
    },
    "Archive Contract Insight history"
  );
}

export async function restoreContractInsightHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/history/${encodeURIComponent(id)}/restore`,
    {
      method: "PATCH",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: false }),
    },
    "Restore Contract Insight history"
  );
}

export async function deleteContractInsightHistory(jobId, opts = {}) {
  const id = requireJobId(jobId);

  return fetchJson(
    `/history/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      idToken: opts.idToken,
    },
    "Delete Contract Insight history"
  );
}

export async function getContractInsightReport(jobId, opts = {}) {
  return getReport(jobId, opts);
}

export async function getContractInsightDashboard(jobId, opts = {}) {
  const id = requireJobId(jobId);
  const base = {
    ok: true,
    job_id: id,
    jobId: id,
    dashboard: {},
    errors: {},
  };

  const [statusResult, liveResult, sourceResult, reportResult] = await Promise.allSettled([
    getContractInsightStatus(id, opts),
    getLiveView(id, opts),
    getSourceFile(id, opts),
    getReport(id, opts),
  ]);

  if (statusResult.status === "fulfilled") {
    const statusPayload = normalizeJobStatusPayload(statusResult.value, id);
    base.statusPayload = statusPayload;
    base.status = statusPayload?.status || statusPayload?.job?.status || "";
    base.job = statusPayload?.job || {};
    base.progress = statusPayload?.progress || {};
  } else {
    base.errors.status = statusResult.reason?.message || "Unable to load status.";
  }

  if (liveResult.status === "fulfilled") {
    const livePayload = liveResult.value || {};
    const raw = livePayload?.liveView || livePayload?.live_view || livePayload?.thread || livePayload || {};
    const liveView = (raw && (raw.liveView || raw.live_view)) || raw;

    base.livePayload = livePayload;
    base.liveView = liveView || {};
    base.live_view = liveView || {};
    base.thread = livePayload?.thread || {};
    base.risk = livePayload?.risk || livePayload?.riskPreview || {};
    base.riskPreview = livePayload?.riskPreview || livePayload?.risk || {};
  } else {
    base.errors.liveView = liveResult.reason?.message || "Unable to load live view.";
  }

  if (sourceResult.status === "fulfilled") {
    base.source = sourceResult.value || {};
    base.sourceFile = sourceResult.value || {};
    base.source_file = sourceResult.value || {};
  } else {
    base.errors.source = sourceResult.reason?.message || "Unable to load source file.";
  }

  if (reportResult.status === "fulfilled") {
    const reportPayload = reportResult.value || {};
    base.reportPayload = reportPayload;
    base.report = reportPayload?.report || {};
    base.artifacts = reportPayload?.artifacts || {};

    const coverageSummary = normalizeContractInsightCoverageSummary(reportPayload);
    base.coverageSummary = coverageSummary;
    base.coverage_summary = coverageSummary;

    const reportArtifactLinks = getContractInsightReportArtifactLinks(reportPayload);
    base.reportPdfUrl = reportArtifactLinks.summaryReportUrl || null;
    base.report_pdf_url = reportArtifactLinks.summaryReportUrl || null;
    base.liveViewPdfUrl = reportArtifactLinks.pageReviewUrl || null;
    base.live_view_pdf_url = reportArtifactLinks.pageReviewUrl || null;
    base.terminologyPdfUrl = reportArtifactLinks.termsGuideUrl || null;
    base.terminology_pdf_url = reportArtifactLinks.termsGuideUrl || null;
    base.reportPdfPath = reportArtifactLinks.summaryReportPath || null;
    base.report_pdf_path = reportArtifactLinks.summaryReportPath || null;
    base.liveViewPdfPath = reportArtifactLinks.pageReviewPath || null;
    base.live_view_pdf_path = reportArtifactLinks.pageReviewPath || null;
    base.terminologyPdfPath = reportArtifactLinks.termsGuidePath || null;
    base.terminology_pdf_path = reportArtifactLinks.termsGuidePath || null;
    base.reportJsonUrl = reportPayload?.reportJsonUrl || reportPayload?.report_json_url || null;
    base.report_json_url = reportPayload?.report_json_url || reportPayload?.reportJsonUrl || null;
  } else {
    base.errors.report = reportResult.reason?.message || "Unable to load report.";
  }

  const hasAnyPayload =
    Boolean(base.job && Object.keys(base.job).length) ||
    Boolean(base.liveView && Object.keys(base.liveView).length) ||
    Boolean(base.source && Object.keys(base.source).length) ||
    Boolean(base.report && Object.keys(base.report).length) ||
    Boolean(base.artifacts && Object.keys(base.artifacts).length);

  if (!hasAnyPayload) {
    const message =
      base.errors.status ||
      base.errors.liveView ||
      base.errors.source ||
      base.errors.report ||
      "Unable to load Contract Insight dashboard.";
    const err = new Error(message);
    err.response = base;
    throw err;
  }

  return base;
}

export function pollReport(jobId, { intervalMs = 1500, onTick, idToken, getIdToken } = {}) {
  let timer = null;
  let stopped = false;

  async function resolveIdToken() {
    if (typeof getIdToken === "function") {
      try {
        const fresh = await getIdToken();
        if (fresh) return fresh;
      } catch (_) {
        // Fall back to the route-state token while Firebase Auth finishes hydrating.
      }
    }
    return idToken;
  }

  async function tick() {
    if (stopped) return;
    try {
      const token = await resolveIdToken();
      const payload = await getContractInsightStatus(jobId, { idToken: token });
      safeInvoke(onTick, payload);
      const status = String(payload?.status || payload?.job?.status || "").toLowerCase();
      if (["complete", "completed", "report_ready", "error", "failed", "failure", "billing_error", "canceled", "abandoned"].includes(status)) {
        stop();
      } else {
        timer = setTimeout(tick, intervalMs);
      }
    } catch (err) {
      safeInvoke(onTick, { error: err.message || "poll error" });
      timer = setTimeout(tick, intervalMs * 2);
    }
  }

  function stop() {
    stopped = true;
    if (timer) clearTimeout(timer);
  }

  tick();
  return stop;
}

// Compatibility wrappers retained for older imports during rolling deployment.
export async function getUploadUrl(meta, opts = {}) {
  const jobId = meta?.jobId || meta?.job_id;
  if (!jobId) {
    throw new Error("getUploadUrl now requires jobId. Create the Contract Insight job first.");
  }
  return requestUploadUrl(jobId, meta, opts);
}

export async function uploadToSignedUrl(url, file, headers = {}, opts = {}) {
  return putFileToSignedUrl(url, file, headers, opts);
}

export async function startReview({ jobId, payload = {}, ...legacy }, opts = {}) {
  return startContractInsightRun(jobId, { ...payload, ...legacy }, opts);
}

export async function submitContractFile(file, opts = {}) {
  const payload = opts.payload || {};
  const created = await createContractInsightJob(payload, opts);
  const jobId = created?.job_id || created?.jobId || created?.id;
  if (!jobId) throw new Error("Backend did not return a Contract Insight job id.");

  const uploaded = await uploadContractInsightFile(jobId, file, opts.fileMeta || {}, opts);
  const started = await startContractInsightRun(jobId, payload, opts);
  return {
    ok: true,
    jobId: started?.jobId || started?.job_id || jobId,
    job_id: started?.job_id || started?.jobId || jobId,
    storagePath: uploaded?.storagePath || uploaded?.storage_path || "",
    uploaded,
    started,
  };
}

const contractApi = {
  assertContractInsightUploadFile,
  createContractInsightJob,
  abandonContractInsightJob,
  uploadContractInsightFile,
  startContractInsightRun,
  getContractInsightStatus,
  isContractInsightAlreadyStartedError,
  getContractInsightUploadUserMessage,
  getUploadUrl,
  uploadToSignedUrl,
  startReview,
  getReport,
  getContractInsightReportArtifactLinks,
  getContractInsightReportArtifacts,
  createContractInsightBrowserTicket,
  listContractThreads,
  getLiveView,
  getSourceFile,
  listContractInsightHistory,
  getContractInsightHistoryStats,
  getContractInsightHistory,
  renameContractInsightHistory,
  archiveContractInsightHistory,
  restoreContractInsightHistory,
  deleteContractInsightHistory,
  getContractInsightReport,
  getContractInsightDashboard,
  pollReport,
  submitContractFile,
};

export default contractApi;
